/**
 * cellPresence — presence-density cell classifier (scan overhaul v2, Phase 2).
 *
 * REPLACES the hue-histogram cosine-similarity matcher in `cellSpriteMatcher.ts`
 * for the RUNTIME caught/seen/empty decision. That matcher was a pre-Vision
 * (Tesseract-era) heuristic: it compared each cell's sprite ROI against a
 * pre-computed PokeAPI colour fingerprint and returned "chroma-match" when the
 * hue palette lined up. On PiP-blended cells that produced confident
 * false-positives (device log 2026-07-07: dex #12/#15/#128/#144 all
 * false-applied via chroma-match at pillOverlapFraction 0.32-0.61) because the
 * fingerprint is coarse (8 hue bins) and a semi-transparent overlay preserves
 * the dominant palette.
 *
 * Now Vision reads the dex NUMBER at ~100% confidence, so we already know WHICH
 * species each cell represents. The classifier's only remaining job is: given a
 * cell we know is dex #N in variant X, does this cell look CAUGHT, SEEN-ONLY
 * (silhouette), or NEVER-SEEN (empty)? That is a pure PRESENCE-DENSITY signal —
 * no fingerprint database, no species-specific match, deterministic.
 *
 * Signal design:
 *   - chromaDensity  — fraction of the ROI reading as chroma foreground vs the
 *     cell's own sampled background (from `analyzeSpriteChroma`). This is the
 *     "is there a coloured sprite here?" signal.
 *   - silhouetteScore — fraction of the FOREGROUND that reads near-grey /
 *     monochrome. A PoGo seen-only silhouette is a single-tint grey body: it has
 *     foreground present but almost no colour. High silhouetteScore + moderate
 *     chroma = silhouette; low silhouetteScore + high chroma = a real caught
 *     sprite.
 *   - auraScore — for shadow/purified only, the positive caught-aura marker from
 *     `detectAura` (purple flame / teal starburst). A dark or pale aura catch
 *     can read low chroma yet is unambiguously CAUGHT via its aura.
 *
 * BINARY variants (lucky/perfect/xxs/xxl) are sprite-or-nothing: no silhouette
 * seen-only state exists on those pages, so they collapse to caught|empty.
 *
 * Pure: takes an already-cropped RGBA sprite ROI + variant, returns a verdict.
 * No DOM, no I/O, no fingerprint lookup. Tests drive it directly.
 */

import type { PokedexVariant } from "./pogoPokedexVariantClassifier";
import {
  analyzeSpriteChroma,
  detectAura,
  hasAuraMarker,
  estimateLocalCellBackground,
  edgeDensityFromRgba,
  estimateBackgroundRgb,
} from "./ocrPixelOps";
import { BINARY_VARIANTS } from "./cellSpriteMatcher";

/** Terminal presence state for a cell. */
export type CellPresenceState = "caught" | "silhouette" | "empty";

/** Why the classifier issued its state — surfaced in per-cell device logs. */
export type CellPresenceReason =
  | "sprite-present" // sprite pixels detected in ROI at density above CAUGHT floor
  | "sprite-absent" // no sprite pixels; empty cell
  | "silhouette-grey" // monochrome/grey foreground detected (silhouette)
  | "aura-present" // shadow/purified aura detected (positive caught signal)
  | "borderline" // presence in the marginal band between empty and caught
  | "below-presence-floor" // chroma cleared the floor but ROI foreground magnitude is negligible (empty-tint false-positive guard)
  | "binary-caught-veto" // binary variant: body cleared the flat-fill bar but was neither coloured nor a substantial structured body (decoration / scroll bleed)
  | "binary-flat-interior" // binary variant: the sprite band carries no internal edges, so the "body" was a mis-estimated flat card, not a sprite (inflated-Perfect class, 2026-08-30)
  | "binary-chroma-body" // binary variant: the body estimate saw nothing (sprite tone ~ card tone) but the ROI carries real chroma AND internal edges, which an empty flat card never does (2026-09-08)
  | "legacy-hue-silhouette" // legacy hueVariance analysis identified silhouette (any tint)
  | "legacy-hue-empty"; // legacy hueVariance analysis identified empty ROI

export interface CellPresenceResult {
  state: CellPresenceState;
  reason: CellPresenceReason;
  /** 0..1 confidence in the issued state. */
  confidence: number;
  /** Fraction of foreground pixels in the ROI (0..1). */
  chromaDensity: number;
  /** 0..1, higher = more grey/monochrome foreground (silhouette-like). */
  silhouetteScore: number;
  /** 0..1, only meaningful for shadow/purified (aura marker strength). */
  auraScore: number;
  /**
   * Scan overhaul v2 decision signals (surfaced for device telemetry).
   * `lumStd` = internal luminance std of the body (0..1): the caught-vs-silhouette
   * discriminator (flat fill ~0.01, shaded sprite >0.07). `bodyFrac` = fraction of
   * the ROI that is body after number masking: the empty gate (~0 = no sprite).
   */
  lumStd: number;
  bodyFrac: number;
}

export interface CellPresenceInput {
  /** The cell's sprite ROI, already cropped (top ~62% of the cell). */
  spriteRgba: Uint8ClampedArray;
  width: number;
  height: number;
  variant: PokedexVariant;
  /** Optional local background hue from adjacent-cell chroma estimation. */
  localBackgroundHue?: number;
  /**
   * Absolute sprite-presence magnitude, resolution-normalised: the fraction of
   * the cell's sprite region whose LUMINANCE differs from the sampled background
   * (i.e. `analyzeSpriteHues.foregroundPixels / regionArea`). Threaded in from the
   * caller (which already computes it for the cell classifier). Used ONLY to veto a
   * "caught" verdict when the ROI has negligible real foreground — see
   * {@link PRESENCE_CAUGHT_MIN_FOREGROUND_FRACTION}. Optional so unit tests and
   * callers that cannot supply it keep the pre-existing chroma-only behaviour.
   */
  roiForegroundFraction?: number;
  /**
   * The dex number's bounding box within the sprite ROI, in ROI-relative
   * fractions (x/y/w/h in 0..1), when the OCR engine reported it (Apple Vision
   * path). Layer A of the scan-overhaul-v2 classifier redo: an EMPTY cell shows
   * only its centred dex number, which the top-62% crop includes and which would
   * otherwise read as body foreground. Masking the number box out of the ROI
   * collapses an empty cell to ~zero body so it is not confused with a small
   * caught sprite. Omitted → no masking (a caught/silhouette cell's number sits
   * at the card bottom, below this ROI, so masking is a no-op for them anyway).
   */
  numberBox?: { x: number; y: number; w: number; h: number };
  /**
   * The ROI is the card band directly ABOVE the Vision number box (see
   * cardBandAboveNumber in pokedexOcr): no digits inside it and no
   * neighbouring card, so a blank card reads chroma exactly 0 (2026-09-24
   * Perfect sweep, 9 frames x 8 lattice offsets). Binary pages then accept
   * on chroma + internal edges over the WHOLE band.
   */
  numberAnchored?: boolean;
}

/* ── Tunable constants (all exported) ─────────────────────────────────────
 *
 * These thresholds are the presence-density calibration. They are seeded from
 * the ocrPixelOps primitives' own empirical floors (analyzeSpriteChroma's empty
 * fraction 0.015, localBgPresence's deviation floor) but chosen higher for the
 * CAUGHT floor so a PiP-blended fringe or the dex-number glyph band does not
 * alone read "caught". They MUST be validated against real device fixtures — see
 * WARNINGS in the overhaul report. On-device tuning is expected.
 * ──────────────────────────────────────────────────────────────────────── */


/**
 * Minimum ABSOLUTE sprite-presence (as a fraction of the sprite region's area)
 * required for a chroma-density "caught" verdict on a three-state page.
 *
 * WHY THIS EXISTS (2026-07-08, empty-cell false-write fix, dex #165/#166/#214).
 * `chromaFraction` is a FRACTION — a nearly-empty crop over a strongly-tinted page
 * can read a high chroma fraction from a flat colour cast (many pixels sit >60 RGB
 * from the sampled background yet the crop carries almost no real sprite). Telemetry
 * (`2026-07-08_06561cc_shiny-kanto_stall+falsewrites.txt`) proved it: empty johto
 * cells #165/#166 reached `spriteVerdict:"caught"` at chromaFraction 0.077-0.135
 * — HIGHER than several genuine catches in the SAME frame (#153 0.056, #158 0.053).
 * Because those cells share one frame (one tile geometry), `chromaForegroundPixels`
 * is just `chromaFraction × constant`, so a chroma-count floor would block the real
 * catches BEFORE the false ones — chroma magnitude cannot separate them.
 *
 * The signal that DOES separate is the LUMINANCE-structure foreground magnitude
 * (`analyzeSpriteHues.foregroundPixels`): a flat colour cast has little luminance
 * structure, a real sprite has lots. Confirming false reads: #165 = 183-190 px,
 * #166 = 70-72 px over a ~41.7k-px region (fraction 0.0017-0.0046). Genuine applied
 * catches: #159 = 268 px (0.0064), #163 = 537 px (0.013), and the task's protected
 * kanto shinies fg 2290-6499 (0.055-0.156). A floor of 0.0055 sits between the false
 * crops (≤0.0046) and the lowest genuine catch (#159 0.0064): it blocks #165/#166 by
 * ~1.2x, preserves #159 by ~1.2x, and clears the protected kanto catches by ~10x.
 *
 * Expressed as a FRACTION (not an absolute px count) so it is resolution-independent
 * across capture scales/devices; the caller normalises by the sprite-region area.
 * The margin against the lowest johto catch is deliberately tight (the sprite-crop
 * populations genuinely overlap — see the OCR pipeline review §B4); it is placed to
 * flip ONLY the confirmed empty crops while preserving every genuine catch in the
 * telemetry. Scoped to three-state pages (see classifyCellPresence): the aura
 * (shadow/purified) and binary caught paths are left intact.
 */
export const PRESENCE_CAUGHT_MIN_FOREGROUND_FRACTION = 0.0055;

/**
 * Per-pixel saturation below which a foreground pixel is "near-grey". Chroma =
 * (max-min)/255 in 0..1; a monochrome PoGo silhouette body sits well under this
 * while a full-colour sprite body exceeds it across most of its pixels. Kept in
 * this module (not exported) so the silhouette signal is self-contained.
 */
const GREY_SATURATION_CEILING = 0.12;

/**
 * Grey-foreground fraction over the ROI.
 *
 * Returns the fraction of NON-background pixels whose saturation is below
 * {@link GREY_SATURATION_CEILING}. A background pixel is one close to the
 * locally-sampled corner/edge background; we reuse the same corner-median idea
 * as `estimateLocalCellBackground` inline so this stays a leaf helper. When
 * there is essentially no foreground the score is 0 (an empty cell is not a
 * "grey silhouette").
 */
function greyForegroundFraction(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number {
  if (width < 2 || height < 2) return 0;
  // Sample the outer ring as background (corners + edges are reliably the
  // cell's background field across every PoGo page tint).
  const ring = Math.max(1, Math.round(Math.min(width, height) * 0.1));
  let br = 0;
  let bg = 0;
  let bb = 0;
  let bn = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const onRing =
        x < ring || x >= width - ring || y < ring || y >= height - ring;
      if (!onRing) continue;
      const i = (y * width + x) * 4;
      br += data[i];
      bg += data[i + 1];
      bb += data[i + 2];
      bn++;
    }
  }
  if (bn === 0) return 0;
  const bgR = br / bn;
  const bgG = bg / bn;
  const bgB = bb / bn;
  const bgDist = 60; // matches analyzeSpriteChroma DEFAULT_CHROMA_BG_DISTANCE
  const bgDistSq = bgDist * bgDist;

  let foreground = 0;
  let grey = 0;
  for (let y = 0; y < height; y++) {
    for (let x = 0; x < width; x++) {
      const i = (y * width + x) * 4;
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      const dr = r - bgR;
      const dg = g - bgG;
      const db = b - bgB;
      if (dr * dr + dg * dg + db * db <= bgDistSq) continue; // background pixel
      foreground++;
      const max = Math.max(r, g, b);
      const min = Math.min(r, g, b);
      const sat = (max - min) / 255;
      if (sat < GREY_SATURATION_CEILING) grey++;
    }
  }
  return foreground > 0 ? grey / foreground : 0;
}

/* ── Scan overhaul v2 body-structure classifier ───────────────────────────────
 *
 * The chroma-density approach could not separate caught / silhouette / empty:
 * on real device pixels a full-colour caught sprite and a flat tan/grey
 * silhouette read the SAME chroma (both are "foreground present"). The signal
 * that DOES separate them, validated on the labelled shiny corpus, is the
 * INTERNAL LUMINANCE STANDARD DEVIATION of the body:
 *   - a SILHOUETTE is a single flat fill (one tone, no shading)  -> lumStd ~0.01
 *   - a CAUGHT sprite has internal shading/detail/features        -> lumStd >0.07
 * and BODY-FILL FRACTION separates empty (no body, just the masked number) from
 * a real — even small/pale — sprite. Corpus gap: silhouettes ≤0.013, caught
 * ≥0.073, so a lumStd bar at 0.04 is clean with wide margin either side.
 * ─────────────────────────────────────────────────────────────────────────── */

/** bodyFrac below this (after number masking) = no sprite body → empty. */
export const PRESENCE_BODY_PRESENT_FLOOR = 0.012;
/** Internal body luminance-std below this = a flat uniform fill → silhouette. */
export const PRESENCE_FLAT_FILL_LUMSTD_CEILING = 0.04;

/**
 * Binary-variant caught gate (lucky/perfect/xxs/xxl). Those pages render an
 * uncaught entry as a BLANK card (no silhouette), over heavy page decoration
 * (Lucky's orange sparkles/dots). A body that merely clears the flat-fill bar can
 * therefore be DECORATION or scroll-motion bleed, not a sprite — device telemetry
 * (Lucky #834/#843, 2026-07-11) false-applied at bodyFrac ~0.05, vs a real catch
 * (#828) at bodyFrac 0.45.
 *
 * The clean separator is BODY-FILL FRACTION: a real sprite fills a substantial
 * part of the cell whatever its colour; decoration / scroll bleed does not.
 * lumStd is NOT usable here — a genuinely grey/pale sprite carries about the same
 * modest lumStd (~0.09) as the decoration, so it can't separate them. A REAL
 * binary catch therefore needs EITHER a substantial body (grey or coloured) OR a
 * smaller-but-clearly-COLOURED body (rejects tiny high-chroma sparkles, which have
 * negligible body). These are settings-invariant: the frame is the game's rendered
 * buffer (brightness / Night Shift / True Tone never touch it). NOT applied to
 * non-binary pages, which keep their silhouette state.
 */
export const PRESENCE_BINARY_CAUGHT_SUBSTANTIAL_BODYFRAC = 0.13;
export const PRESENCE_BINARY_CAUGHT_COLOUR_BODYFRAC = 0.06;
export const PRESENCE_BINARY_CAUGHT_CHROMA_FLOOR = 0.12;

/**
 * Binary flat-interior veto (inflated-Perfect heal, 2026-08-30).
 *
 * Every body signal above is measured against `estimateLocalCellBackground`,
 * whose outer-ring median can land in the INTER-CARD GUTTER instead of the
 * card face. When that happens on a washed page (the acc6 Perfect report:
 * peach card on peach page), the entire empty card deviates from the "background"
 * and reads as one huge structured body — bodyFrac and lumStd both sail past
 * their floors and an EMPTY card classifies caught. The Perfect dex then fills
 * with everything the trainer scrolled past.
 *
 * Edge density is the one presence cue that does not depend on the background
 * estimate at all (see edgeDensityFromRgba): a flat card interior has near-zero
 * internal luminance gradient whatever colour it is, while a real sprite always
 * carries silhouette-boundary and shading edges. So for binary variants a
 * caught verdict must ALSO show edges in the SPRITE BAND (the ROI above the
 * number-mask band, top 60%), else the "body" was a mis-estimated flat card.
 *
 * Floor calibrated over the real labelled corpus (client/src/test/fixtures/dex,
 * IMG_1200/1202 perfect + IMG_1204 xxl + IMG_1207 xxs): every real caught cell
 * measured >= 0.084; empty cards measured 0.020-0.064 (perfect), 0.020-0.034
 * (xxs). 0.04 sits at 2x margin below the weakest real catch.
 */
export const PRESENCE_BINARY_FLAT_INTERIOR_EDGE_FLOOR = 0.04;
/**
 * Binary chroma-body acceptance (2026-09-08, Perfect-dex device report).
 * The body estimate leans on luminance deviation from the card, and on the
 * salmon 100% page a tan Lillipup or a pink Flamigo deviates so little that
 * bodyFrac reads ~0 and the sprite-absent cut fires on a real catch (16 of
 * ~40 visible catches registered). Chroma does not have that problem: the
 * card is a flat tint, so an EMPTY binary cell measures chroma 0.000-0.002
 * across every frame in the report while every real catch measured
 * 0.016-0.143. Accept a caught verdict on chroma alone when the sprite band
 * also carries internal edges (a sparkle decoration is tiny and flat; a
 * sprite is neither). Floors sit 5x above the empties and under the
 * weakest real catch (Lillipup 0.016 / edge 0.034).
 */
export const PRESENCE_BINARY_CHROMA_BODY_CHROMA_FLOOR = 0.012;
export const PRESENCE_BINARY_CHROMA_BODY_EDGE_FLOOR = 0.03;
/**
 * ...and the deviating pixels must be COLOURED: a grey page decoration or a
 * flat grey blob (the Lucky #834/#843 class) deviates from the card and has
 * an outline, but has no saturation. Fraction of the ROI that both deviates
 * from the card colour and carries real saturation, and the saturation bar.
 */
export const PRESENCE_BINARY_CHROMA_BODY_SATURATED_FLOOR = 0.008;
export const PRESENCE_BINARY_CHROMA_BODY_MIN_SATURATION = 0.28;
const CHROMA_BODY_BG_DISTANCE = 30;

/** Fraction of pixels that differ from the card colour AND are saturated. */
export function saturatedForegroundFraction(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
): number {
  if (width < 4 || height < 4) return 0;
  const bg = estimateBackgroundRgb(data, width, height);
  const distSq = CHROMA_BODY_BG_DISTANCE * CHROMA_BODY_BG_DISTANCE;
  let hit = 0;
  const total = width * height;
  for (let i = 0; i < data.length; i += 4) {
    const r = data[i], g = data[i + 1], b = data[i + 2];
    const dr = r - bg.r, dg = g - bg.g, db = b - bg.b;
    if (dr * dr + dg * dg + db * db < distSq) continue;
    const max = Math.max(r, g, b);
    if (max === 0) continue;
    const sat = (max - Math.min(r, g, b)) / max;
    if (sat >= PRESENCE_BINARY_CHROMA_BODY_MIN_SATURATION) hit += 1;
  }
  return total > 0 ? hit / total : 0;
}
/** Fraction of the sprite ROI (top rows) the edge check reads: the band above the number mask. */
export const PRESENCE_BINARY_EDGE_BAND_FRAC = 0.6;
/**
 * Number-anchored binary acceptance. Blank cards measured chroma 0.0000 and
 * whole-band edge <= 0.031 on every read; the weakest real catches were
 * Zubat #041 (chroma 0.0093, edge 0.038) and Diglett #050 (chroma 0.0116).
 * Venomoth #049 carries almost no saturated pixels (0.004), so the
 * saturation gate is not used on this path.
 */
export const PRESENCE_ANCHORED_CHROMA_FLOOR = 0.004; // 0.005 until 2026-09-24: Diglett #050 measures 0.0049 once the band reaches the card top
export const PRESENCE_ANCHORED_EDGE_FLOOR = 0.035;

/**
 * Body statistics over the sprite ROI, relative to the cell's own local
 * background, EXCLUDING the dex-number box (Layer A) when supplied.
 *
 * Returns the fraction of ROI pixels that are body (deviate from the local
 * background) and the standard deviation of luminance ACROSS those body pixels
 * (0..1). A flat silhouette fill has near-zero lumStd; a real caught sprite,
 * however dark or pale, carries internal luminance structure. Pure leaf helper.
 */
function analyzeBodyStructure(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  numberBox?: { x: number; y: number; w: number; h: number },
): { bodyFrac: number; lumStd: number; bodyPixels: number } {
  if (width < 4 || height < 4) return { bodyFrac: 0, lumStd: 0, bodyPixels: 0 };
  const bg = estimateLocalCellBackground(data, width, height);
  const inset = Math.max(1, Math.round(Math.min(width, height) * 0.1));
  // Number box → pixel bounds (inclusive), or an empty range when absent.
  const nb = numberBox
    ? {
        x0: Math.floor(numberBox.x * width),
        x1: Math.ceil((numberBox.x + numberBox.w) * width),
        y0: Math.floor(numberBox.y * height),
        y1: Math.ceil((numberBox.y + numberBox.h) * height),
      }
    : null;
  const bgDist = 40;
  const distSq = bgDist * bgDist;
  let total = 0;
  let bodyPixels = 0;
  let sum = 0;
  let sumSq = 0;
  for (let y = inset; y < height - inset; y++) {
    for (let x = inset; x < width - inset; x++) {
      // Layer A: skip the dex-number box entirely (it is not body).
      if (nb && x >= nb.x0 && x < nb.x1 && y >= nb.y0 && y < nb.y1) continue;
      total++;
      const i = (y * width + x) * 4;
      const r = data[i], g = data[i + 1], b = data[i + 2];
      const dr = r - bg.r, dg = g - bg.g, db = b - bg.b;
      if (dr * dr + dg * dg + db * db <= distSq) continue; // background pixel
      bodyPixels++;
      const lum = 0.299 * r + 0.587 * g + 0.114 * b;
      sum += lum;
      sumSq += lum * lum;
    }
  }
  const bodyFrac = total > 0 ? bodyPixels / total : 0;
  let lumStd = 0;
  if (bodyPixels > 0) {
    const mean = sum / bodyPixels;
    lumStd = Math.sqrt(Math.max(0, sumSq / bodyPixels - mean * mean)) / 255;
  }
  return { bodyFrac, lumStd, bodyPixels };
}

/**
 * Classify a cell's presence state from its sprite ROI.
 *
 * Scan overhaul v2 (body-structure classifier). The decision is:
 *   1. Aura (shadow/purified) with a real body        -> caught (aura-present)
 *   2. bodyFrac < BODY_PRESENT_FLOOR                    -> empty (sprite-absent)
 *   3. flat body (lumStd < FLAT_FILL_LUMSTD_CEILING)    -> silhouette (three-state)
 *                                                          or empty (binary)
 *   4. structured body                                  -> caught (sprite-present)
 * The number box is masked out of the ROI first (Layer A) so an empty cell's
 * centred number does not read as body. Conservative bias: a body that is not
 * clearly structured is SEEN (silhouette)/empty, never caught.
 *
 * BINARY variants (lucky/perfect/xxs/xxl) have no silhouette state, so a flat
 * body collapses to empty rather than silhouette.
 */
/**
 * A copy of the ROI with the OCR'd number box painted over in the card's own
 * background colour (2026-09-22 Galaxy scan log). On the binary pages a
 * BLANK card prints its number CENTRED, inside the sprite band, and on the
 * tinted Lucky (orange) and XXL / XXS (blue) pages those digits are as
 * saturated and as edged as any sprite. The body reading already masks the
 * number and found bodyFrac 0 on every one of the 40-odd false catches in
 * that log; the chroma-body shortcut below did not mask it and accepted
 * every blank card at chroma 0.014-0.035, confidence 0.86 + chroma. The
 * digit-colour filter inside analyzeSpriteChroma does not catch the
 * Android renderer's exact tones, so the mask is geometric, like the body's.
 * A little padding covers the glyphs' anti-aliased fringe.
 */
function maskNumberBox(
  data: Uint8ClampedArray,
  width: number,
  height: number,
  numberBox: { x: number; y: number; w: number; h: number },
): Uint8ClampedArray {
  const bg = estimateBackgroundRgb(data, width, height);
  const out = new Uint8ClampedArray(data);
  const pad = 0.03;
  const x0 = Math.max(0, Math.floor((numberBox.x - pad) * width));
  const x1 = Math.min(width, Math.ceil((numberBox.x + numberBox.w + pad) * width));
  const y0 = Math.max(0, Math.floor((numberBox.y - pad) * height));
  const y1 = Math.min(height, Math.ceil((numberBox.y + numberBox.h + pad) * height));
  for (let y = y0; y < y1; y++) {
    for (let x = x0; x < x1; x++) {
      const i = (y * width + x) * 4;
      out[i] = bg.r;
      out[i + 1] = bg.g;
      out[i + 2] = bg.b;
      out[i + 3] = 255;
    }
  }
  return out;
}

/** Fraction of the anchored band's width holding the shadow flame / purified sparkle icon (its left column). */
export const ANCHORED_ICON_COLUMN_FRAC = 0.3;
/**
 * Flat-fill ceiling for the anchored three-state band. The seen-only
 * silhouette measured lumStd 0.008 (Purified #024); the smallest real catches
 * in the band 0.041 (Kakuna #014) and 0.054 (Pidgeotto #018 clipped).
 */
export const ANCHORED_FLAT_FILL_LUMSTD_CEILING = 0.03;

/**
 * Three-state pages (Pokémon / Shiny / Mega / G-Max / Shadow / Purified) on the
 * number-anchored card band (2026-09-24 device pass: small low sprites on
 * Shadow / Purified read blank exactly as on Perfect before the band). The
 * shadow flame / purified sparkle icon sits in the card's lower-left, beside
 * the number, so the band's left column is dropped before measuring; what is
 * left is card face plus, on a caught card, the sprite, or, on a seen-only
 * card, the flat grey silhouette.
 *   blank      -> no body and no chroma
 *   silhouette -> a body that is a flat fill (lumStd under the ceiling),
 *                 whatever its tint (a grey blob deviates from a mint card)
 *   caught     -> chroma with internal edges, or a structured body
 */
function classifyAnchoredThreeState(
  spriteRgba: Uint8ClampedArray,
  width: number,
  height: number,
  variant: PokedexVariant,
): CellPresenceResult {
  const cut = Math.floor(width * ANCHORED_ICON_COLUMN_FRAC);
  const w = width - cut;
  const band = new Uint8ClampedArray(w * height * 4);
  for (let y = 0; y < height; y++) {
    band.set(spriteRgba.subarray((y * width + cut) * 4, (y * width + width) * 4), y * w * 4);
  }
  const chroma = analyzeSpriteChroma(band, w, height, { variant });
  const body = analyzeBodyStructure(band, w, height);
  const edge = edgeDensityFromRgba(band, w, height);
  const base = {
    chromaDensity: chroma.chromaFraction,
    silhouetteScore: greyForegroundFraction(band, w, height),
    auraScore: 0,
    lumStd: body.lumStd,
    bodyFrac: body.bodyFrac,
  };
  const hasBody = body.bodyFrac >= PRESENCE_BODY_PRESENT_FLOOR;
  if (!hasBody && chroma.chromaFraction < PRESENCE_ANCHORED_CHROMA_FLOOR) {
    return { state: "empty", reason: "sprite-absent", confidence: 0.9, ...base };
  }
  if (hasBody && body.lumStd < ANCHORED_FLAT_FILL_LUMSTD_CEILING) {
    return { state: "silhouette", reason: "silhouette-grey", confidence: 0.9, ...base };
  }
  if (chroma.chromaFraction >= PRESENCE_ANCHORED_CHROMA_FLOOR && edge >= PRESENCE_ANCHORED_EDGE_FLOOR) {
    return { state: "caught", reason: "sprite-present", confidence: Math.min(0.95, 0.86 + chroma.chromaFraction), ...base };
  }
  if (hasBody) {
    return { state: "caught", reason: "sprite-present", confidence: 0.86, ...base };
  }
  return { state: "empty", reason: "sprite-absent", confidence: 0.7, ...base };
}

export function classifyCellPresence(input: CellPresenceInput): CellPresenceResult {
  const { spriteRgba, width, height, variant, numberBox } = input;
  const binary = BINARY_VARIANTS.has(variant);

  // Every chroma / edge / saturation reading works on the number-masked
  // copy, so a card's own digits can never be its "sprite".
  if (input.numberAnchored && !binary) {
    return classifyAnchoredThreeState(spriteRgba, width, height, variant);
  }
  const chromaRgba = numberBox ? maskNumberBox(spriteRgba, width, height, numberBox) : spriteRgba;
  const chroma = analyzeSpriteChroma(chromaRgba, width, height, { variant });
  const chromaDensity = chroma.chromaFraction;
  const silhouetteScore = greyForegroundFraction(spriteRgba, width, height);
  const body = analyzeBodyStructure(spriteRgba, width, height, numberBox);

  // Aura is a positive caught signal for shadow/purified only: a dark shadow or
  // pale purified catch can carry little internal luminance structure yet an
  // unambiguous aura marker (purple flame / teal starburst). Require a real body
  // too, so an aura-only glow bleeding from a neighbouring cell or page chrome
  // can never false-apply on an essentially-empty ROI.
  let auraScore = 0;
  if (hasAuraMarker(variant)) {
    const aura = detectAura(spriteRgba, width, height, variant);
    auraScore = aura.present ? Math.max(aura.confidence, 0.5) : aura.confidence;
    if (aura.present && body.bodyFrac >= PRESENCE_BODY_PRESENT_FLOOR) {
      // The aura may only RAISE confidence (2026-09-24 Purified device pass:
      // every catch held at 0.60-0.85 as "unclean-read"). The purified
      // sparkle / shadow flame covers a few percent of the lower-left region,
      // so aura.confidence lands under the 0.85 apply bar, and this early
      // return used to skip the structure test below that scores the same
      // sprites 0.88-1.0. `structured` is exactly that test's confidence, so
      // no cell clears the bar here that the plain sprite-present rule would
      // not; a flat (silhouette) body still gets 0 from it.
      const structured = body.lumStd >= PRESENCE_FLAT_FILL_LUMSTD_CEILING
        ? Math.min(1, 0.8 + Math.min(1, (body.lumStd - PRESENCE_FLAT_FILL_LUMSTD_CEILING) / 0.1) * 0.2)
        : 0;
      return {
        state: "caught",
        reason: "aura-present",
        confidence: Math.max(0.6, Math.min(1, aura.confidence), structured),
        chromaDensity,
        silhouetteScore,
        auraScore,
        lumStd: body.lumStd,
        bodyFrac: body.bodyFrac,
      };
    }
  }

  // [0b] Binary chroma-body acceptance (see PRESENCE_BINARY_CHROMA_BODY_*):
  // a sprite whose tone hides from the luminance body estimate still shows
  // chroma and structure a blank card cannot.
  let binaryBandEdge: number | null = null;
  if (binary) {
    const anchored = !!input.numberAnchored;
    const bandH = anchored ? height : Math.max(4, Math.floor(height * PRESENCE_BINARY_EDGE_BAND_FRAC));
    binaryBandEdge = edgeDensityFromRgba(chromaRgba, width, bandH);
    const chromaBodyOk = anchored
      ? chromaDensity >= PRESENCE_ANCHORED_CHROMA_FLOOR && binaryBandEdge >= PRESENCE_ANCHORED_EDGE_FLOOR
      : chromaDensity >= PRESENCE_BINARY_CHROMA_BODY_CHROMA_FLOOR &&
        binaryBandEdge >= PRESENCE_BINARY_CHROMA_BODY_EDGE_FLOOR &&
        saturatedForegroundFraction(chromaRgba, width, height) >= PRESENCE_BINARY_CHROMA_BODY_SATURATED_FLOOR;
    if (chromaBodyOk) {
      // Confidence sits ABOVE the session's apply bar (SPRITE_APPLY_THRESHOLD
      // 0.85) by construction: this path only fires on a calibrated
      // three-signal read whose empties measure 0.000-0.002, and the first
      // cut's 0.6 + 2*chroma (0.63-0.78 for a Lillipup / Herdier) parked
      // every such catch in the review band, so only the big high-chroma
      // sprites ever applied (third device log, 2026-09-08).
      return {
        state: "caught",
        reason: "binary-chroma-body",
        confidence: Math.min(0.95, 0.86 + chromaDensity),
        chromaDensity,
        silhouetteScore,
        auraScore,
        lumStd: body.lumStd,
        bodyFrac: body.bodyFrac,
      };
    }
  }
  // [1] No sprite body — a blank card whose only foreground was the (now masked)
  // centred dex number. Empty / never-seen.
  if (body.bodyFrac < PRESENCE_BODY_PRESENT_FLOOR) {
    return {
      state: "empty",
      reason: "sprite-absent",
      confidence: Math.min(1, 0.8 + (PRESENCE_BODY_PRESENT_FLOOR - body.bodyFrac) * 10),
      chromaDensity,
      silhouetteScore,
      auraScore,
      lumStd: body.lumStd,
      bodyFrac: body.bodyFrac,
    };
  }

  // [2] A body IS present but it is a FLAT, uniform fill (near-zero internal
  // luminance variation) — a PoGo seen-only silhouette, whatever its tint
  // (tan on shiny, grey on purified). Binary variants have no silhouette state,
  // so a flat body there collapses to empty rather than reading caught.
  if (body.lumStd < PRESENCE_FLAT_FILL_LUMSTD_CEILING) {
    if (binary) {
      return {
        state: "empty",
        reason: "sprite-absent",
        confidence: 0.6,
        chromaDensity,
        silhouetteScore,
        auraScore,
        lumStd: body.lumStd,
        bodyFrac: body.bodyFrac,
      };
    }
    return {
      state: "silhouette",
      reason: "silhouette-grey",
      confidence: Math.min(0.95, 0.6 + (PRESENCE_FLAT_FILL_LUMSTD_CEILING - body.lumStd) * 8),
      chromaDensity,
      silhouetteScore,
      auraScore,
      lumStd: body.lumStd,
      bodyFrac: body.bodyFrac,
    };
  }

  // [3] A body with real internal luminance STRUCTURE (shading / detail /
  // features) — a full caught sprite, however dark or pale.
  //
  // Binary-variant veto: on lucky/perfect/xxs/xxl an uncaught cell is BLANK, so a
  // body that cleared the flat-fill bar but does not fill enough of the cell is
  // page decoration / scroll bleed, not a sprite — reject it as empty. A real
  // catch is either a substantial body (any colour) or a smaller clearly-coloured
  // body; tiny high-chroma sparkles fail both (negligible body). (Lucky #834/#843.)
  const caughtBySubstantialBody =
    body.bodyFrac >= PRESENCE_BINARY_CAUGHT_SUBSTANTIAL_BODYFRAC;
  const caughtByColouredBody =
    body.bodyFrac >= PRESENCE_BINARY_CAUGHT_COLOUR_BODYFRAC &&
    chromaDensity >= PRESENCE_BINARY_CAUGHT_CHROMA_FLOOR;
  if (binary && !caughtBySubstantialBody && !caughtByColouredBody) {
    return {
      state: "empty",
      reason: "binary-caught-veto",
      confidence: 0.6,
      chromaDensity,
      silhouetteScore,
      auraScore,
      lumStd: body.lumStd,
      bodyFrac: body.bodyFrac,
    };
  }

  // [3b] Binary flat-interior veto: the body floors above all lean on the local
  // background estimate, which the inter-card gutter can poison (an empty card
  // then reads as one huge "body" — the inflated-Perfect class). Edge density
  // over the sprite band is background-independent: a real sprite always
  // carries internal edges there, a flat card never does. Rows are contiguous
  // in an RGBA buffer, so passing a reduced height reads exactly the top band.
  if (binary) {
    const bandEdge = binaryBandEdge ?? edgeDensityFromRgba(chromaRgba, width, Math.max(4, Math.floor(height * PRESENCE_BINARY_EDGE_BAND_FRAC)));
    if (bandEdge < PRESENCE_BINARY_FLAT_INTERIOR_EDGE_FLOOR) {
      return {
        state: "empty",
        reason: "binary-flat-interior",
        confidence: 0.7,
        chromaDensity,
        silhouetteScore,
        auraScore,
        lumStd: body.lumStd,
        bodyFrac: body.bodyFrac,
      };
    }
  }

  // Confidence scales with how far above the flat-fill bar the structure sits.
  const structureHeadroom = Math.min(1, (body.lumStd - PRESENCE_FLAT_FILL_LUMSTD_CEILING) / 0.1);
  return {
    state: "caught",
    reason: "sprite-present",
    confidence: Math.min(1, 0.8 + structureHeadroom * 0.2),
    chromaDensity,
    silhouetteScore,
    auraScore,
    lumStd: body.lumStd,
    bodyFrac: body.bodyFrac,
  };
}

/**
 * True when the named variant uses the binary sprite-or-nothing cell
 * (lucky/perfect/xxs/xxl). Re-exported from the slim variant-metadata module
 * `cellSpriteMatcher.ts` so runtime callers depend on `cellPresence` alone and
 * never pull in the (now data-only) fingerprint module.
 */
export { isBinaryVariant } from "./cellSpriteMatcher";
